import axiosInstance from './axiosConfig';
import type { GeneratedImage } from './imageGenerationService';

// API base URL for fixing image URLs in development
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8003/api';
const STORAGE_BASE_URL = API_BASE_URL.replace('/api', '');

/**
 * Fix image URL for local development
 */
const fixImageUrl = (url: string): string => {
  if (!url) return url;
  if (url.startsWith('http://localhost/storage/')) {
    return url.replace('http://localhost/storage/', `${STORAGE_BASE_URL}/storage/`);
  }
  return url;
};

export interface LpStyle {
  id: number;
  name: string;
  description: string;
  prompt_template: string;
  thumbnail_url: string | null;
  sort_order: number;
  is_active: boolean;
}

export type LpGenerationMode = 'decorative' | 'image2_overlay';

export interface LpStyleProfile {
  palette: {
    primary: string;
    accent: string;
    base: string;
  };
  font_tone: string;
  mood: string[];
  composition: string;
  headline_style?: string;
  industry_hint?: string;
}

export interface CtaOverlayButton {
  x_pct: number;
  y_pct: number;
  w_pct: number;
  h_pct: number;
  href: string;
  label: string;
  modal_id?: string | null;
}

export interface LpSection {
  type: string;
  title: string;
  content: string;
  image_url?: string;
  order: number;
  image_prompt?: string;
  sr_only_text?: string;
  cta_overlay?: {
    buttons: CtaOverlayButton[];
  };
  modals?: Array<{
    id: string;
    title: string;
    body: string;
  }>;
}

export interface LpProject {
  workflow?: { version: number } | null;
  id: string;
  user_id: number;
  lp_style_id: number | null;
  generation_mode: LpGenerationMode;
  title: string | null;
  product_info: string | null;
  product_data: any | null;
  reference_images: any[] | null;
  style_profile: LpStyleProfile | null;
  wireframe: any | null;
  sections: LpSection[] | null;
  design_html: string | null;
  design_css: string | null;
  design_js: string | null;
  status: 'draft' | 'wireframe_generating' | 'wireframe_done' | 'design_generating' | 'design_done' | 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectData {
  product_info: string;
  lp_style_id?: number;
  generation_mode?: LpGenerationMode;
  product_data?: any;
  reference_images?: any[];
}

export interface UpdateSectionData {
  title?: string;
  content?: string;
  image_url?: string;
}

export interface UpdateSectionOverlayData {
  cta_overlay?: {
    buttons: CtaOverlayButton[];
  };
  sr_only_text?: string;
}

class LpGenerationService {
  /**
   * Get available LP styles
   */
  async getStyles(): Promise<{ success: boolean; data: LpStyle[] }> {
    const response = await axiosInstance.get<{ success: boolean; data: LpStyle[] }>(
      '/lp/styles'
    );
    return response.data;
  }

  /**
   * Create a new LP project (triggers wireframe generation)
   */
  async createProject(data: CreateProjectData): Promise<{ success: boolean; data: LpProject; error?: string }> {
    const response = await axiosInstance.post<{ success: boolean; data: LpProject; error?: string }>(
      '/lp/projects',
      data
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Get user's LP projects
   */
  async getProjects(): Promise<{ success: boolean; data: LpProject[] }> {
    const response = await axiosInstance.get<{ success: boolean; data: LpProject[] }>(
      '/lp/projects'
    );

    if (response.data.success && response.data.data) {
      response.data.data = response.data.data.map(project => this.fixProjectUrls(project));
    }

    return response.data;
  }

  /**
   * Get a specific LP project
   */
  async getProject(projectId: string): Promise<{ success: boolean; data: LpProject }> {
    const response = await axiosInstance.get<{ success: boolean; data: LpProject }>(
      `/lp/projects/${projectId}`
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Update a section of an LP project
   */
  async updateSection(
    projectId: string,
    sectionIndex: number,
    data: UpdateSectionData
  ): Promise<{ success: boolean; data: LpProject }> {
    const response = await axiosInstance.put<{ success: boolean; data: LpProject }>(
      `/lp/projects/${projectId}/sections/${sectionIndex}`,
      data
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Update CTA overlay and screen-reader text for an LP section
   */
  async updateSectionOverlay(
    projectId: string,
    sectionIndex: number,
    data: UpdateSectionOverlayData
  ): Promise<{ success: boolean; data: LpProject }> {
    const response = await axiosInstance.put<{ success: boolean; data: LpProject }>(
      `/lp/projects/${projectId}/sections/${sectionIndex}/overlay`,
      data
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Register an LP section image as a GeneratedImage so it can be edited.
   */
  async registerSectionImage(
    projectId: string,
    sectionIndex: number
  ): Promise<{ success: boolean; data: GeneratedImage }> {
    const response = await axiosInstance.post<{ success: boolean; data: GeneratedImage }>(
      `/lp/projects/${projectId}/sections/${sectionIndex}/register-image`
    );

    if (response.data.success && response.data.data) {
      response.data.data = {
        ...response.data.data,
        url: fixImageUrl(response.data.data.url),
      };
    }

    return response.data;
  }

  /**
   * Generate design from wireframe
   */
  async generateDesign(projectId: string): Promise<{ success: boolean; data: LpProject; error?: string }> {
    const response = await axiosInstance.post<{ success: boolean; data: LpProject; error?: string }>(
      `/lp/projects/${projectId}/generate-design`
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Get generation status of an LP project
   */
  async getGenerationStatus(projectId: string): Promise<{ success: boolean; data: LpProject }> {
    const response = await axiosInstance.get<{ success: boolean; data: LpProject }>(
      `/lp/projects/${projectId}/status`
    );

    if (response.data.success && response.data.data) {
      response.data.data = this.fixProjectUrls(response.data.data);
    }

    return response.data;
  }

  /**
   * Get preview HTML of an LP project
   */
  async getPreview(projectId: string): Promise<{ success: boolean; data: { html: string; css: string; js: string } }> {
    const response = await axiosInstance.get<{ success: boolean; data: { html: string; css: string; js: string } }>(
      `/lp/projects/${projectId}/preview`
    );
    return response.data;
  }

  /**
   * Delete an LP project
   */
  async deleteProject(projectId: string): Promise<{ success: boolean; message: string }> {
    const response = await axiosInstance.delete<{ success: boolean; message: string }>(
      `/lp/projects/${projectId}`
    );
    return response.data;
  }

  /**
   * Fix image URLs in a project
   */
  private fixProjectUrls(project: LpProject): LpProject {
    const productData = project.product_data && typeof project.product_data === 'object'
      ? {
          ...project.product_data,
          layout_image_url: project.product_data.layout_image_url
            ? fixImageUrl(project.product_data.layout_image_url)
            : project.product_data.layout_image_url,
        }
      : project.product_data;

    return {
      ...project,
      product_data: productData,
      sections: project.sections?.map(section => ({
        ...section,
        image_url: section.image_url ? fixImageUrl(section.image_url) : undefined,
      })) ?? null,
    };
  }
}

export default new LpGenerationService();
